import { useCallback, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { OnboardingChip } from "./OnboardingChip";
import type { ChipOption } from "./options";

export interface ChipGroupProps {
  options: readonly ChipOption[];
  value: string | null;
  onSelect: (id: string) => void;
  /** Names the group for assistive tech, e.g. "What best describes you?". */
  label: string;
}

/**
 * A wrapping row of single-select chips with roving-tabindex arrow navigation.
 * The group takes one tab stop; arrows move between chips and space/enter
 * select through the chips' own native button behaviour.
 *
 * Remount this with a `key` when the option set changes so focus starts fresh
 * on the new row.
 */
export function ChipGroup({
  options,
  value,
  onSelect,
  label,
}: ChipGroupProps): JSX.Element {
  const chipRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [focusIndex, setFocusIndex] = useState(() => {
    const selected = options.findIndex((option) => option.id === value);
    return selected >= 0 ? selected : 0;
  });

  const focusAt = useCallback((index: number) => {
    setFocusIndex(index);
    chipRefs.current[index]?.focus();
  }, []);

  const move = useCallback(
    (delta: number) => {
      const count = options.length;
      if (count === 0) return;
      focusAt((((focusIndex + delta) % count) + count) % count);
    },
    [focusIndex, focusAt, options.length],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          move(1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          move(-1);
          break;
        case "Home":
          event.preventDefault();
          focusAt(0);
          break;
        case "End":
          event.preventDefault();
          focusAt(options.length - 1);
          break;
        default:
          break;
      }
    },
    [move, focusAt, options.length],
  );

  // Keep the roving tab stop in range if the row shrinks.
  const activeIndex = Math.min(focusIndex, Math.max(options.length - 1, 0));

  return (
    <div
      role="group"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className="flex flex-wrap items-center justify-center gap-3"
    >
      {options.map((option, index) => (
        <OnboardingChip
          key={option.id}
          ref={(node) => {
            chipRefs.current[index] = node;
          }}
          label={option.label}
          icon={option.icon}
          selected={option.id === value}
          tabIndex={index === activeIndex ? 0 : -1}
          onSelect={() => {
            setFocusIndex(index);
            onSelect(option.id);
          }}
        />
      ))}
    </div>
  );
}
